/**
 * Status Constants
 * 상태 관련 상수 및 매핑
 *
 * 프로젝트 전체에서 사용되는 상태 값과 레이블을 중앙에서 관리합니다.
 * 하드코딩된 상태 매핑을 제거하고 일관성을 유지합니다.
 */

/**
 * 상담 결과 레이블
 * V197: PENDING 추가 - 웹훅으로 생성된 상담의 초기 상태
 */
export const CONSULT_RESULT_LABEL = {
  PENDING: "대기",
  SUCCESS: "성공",
  FAIL: "실패",
} as const;

/**
 * 상담 결과에 따른 Badge variant
 * V197: PENDING 추가
 */
export const CONSULT_RESULT_VARIANT = {
  PENDING: "outline",
  SUCCESS: "default",
  FAIL: "destructive",
} as const;

/**
 * 예약 상태 레이블
 */
export const APPOINTMENT_STATUS_LABEL = {
  REQUESTED: "신청",
  CHECKING: "확인중",
  AVAILABLE: "가능",
  NOT_AVAILABLE: "불가",
  CONFIRMED: "확정",
  MODIFICATION_REQUESTED: "변경요청",
  CANCELLATION_REQUESTED: "취소요청",
  COMPLETED: "완료",
  CANCELLED: "취소",
  NO_SHOW: "노쇼",
  SMS_SENT: "안내완료",
  PENDING: "대기", // 레거시
} as const;

/**
 * 예약 상태에 따른 Badge variant
 */
export const APPOINTMENT_STATUS_VARIANT = {
  REQUESTED: "outline",
  CHECKING: "outline",
  AVAILABLE: "outline",
  NOT_AVAILABLE: "destructive",
  CONFIRMED: "default",
  MODIFICATION_REQUESTED: "outline",
  CANCELLATION_REQUESTED: "destructive",
  COMPLETED: "secondary",
  CANCELLED: "destructive",
  NO_SHOW: "destructive",
  SMS_SENT: "secondary",
  PENDING: "outline", // 레거시
} as const;

/**
 * 상담 타입 레이블
 */
export const CONSULT_TYPE_LABEL = {
  RESERVATION: "예약",
  COMPLAINT: "불만", // V1048+: CLAIM → COMPLAINT (백엔드 통일)
  GENERAL: "일반",
  LOCATION_INQUIRY: "위치 안내",
  OTHER: "기타",
} as const;

/**
 * 상담 타입에 따른 Badge variant
 */
export const CONSULT_TYPE_VARIANT = {
  RESERVATION: "default",
  COMPLAINT: "destructive", // V1048+: CLAIM → COMPLAINT
  GENERAL: "outline",
  LOCATION_INQUIRY: "secondary",
  OTHER: "outline",
} as const;

/**
 * 상담 처리 상태 레이블 (백엔드 Consult.ConsultStatus enum 통일)
 */
export const CONSULT_STATUS_LABEL = {
  PENDING: "대기",
  IN_PROGRESS: "처리 중",
  CALLBACK_NEEDED: "콜백 필요",
  CALLBACK_COMPLETED: "콜백 완료",
  OUTBOUND_NEEDED: "아웃바운드 필요",
  OUTBOUND_COMPLETED: "아웃바운드 완료",
  COMPLETED: "처리 완료",
  FAILED: "실패",
} as const;

/**
 * 상담 처리 상태에 따른 Badge variant
 */
export const CONSULT_STATUS_VARIANT = {
  PENDING: "outline",
  IN_PROGRESS: "default",
  CALLBACK_NEEDED: "warning",
  CALLBACK_COMPLETED: "secondary",
  OUTBOUND_NEEDED: "destructive",
  OUTBOUND_COMPLETED: "secondary",
  COMPLETED: "secondary",
  FAILED: "destructive",
} as const;

/**
 * 상담 처리 상태 레이블 가져오기
 */
export const getConsultStatusLabel = (
  status: string | null | undefined,
): string => {
  if (!status) return "-";
  return (
    CONSULT_STATUS_LABEL[status as keyof typeof CONSULT_STATUS_LABEL] || status
  );
};

/**
 * 상담 처리 상태 variant 가져오기
 */
export const getConsultStatusVariant = (
  status: string | null | undefined,
): "default" | "secondary" | "destructive" | "outline" | "warning" => {
  if (!status) return "outline";
  return (
    CONSULT_STATUS_VARIANT[status as keyof typeof CONSULT_STATUS_VARIANT] ||
    "outline"
  );
};

/**
 * 아웃바운드 상태 레이블
 */
export const OUTBOUND_STATUS_LABEL = {
  PENDING: "대기",
  IN_PROGRESS: "진행중",
  COMPLETED: "완료",
  FAILED: "실패",
  CANCELLED: "취소",
} as const;

/**
 * 아웃바운드 상태에 따른 Badge variant
 */
export const OUTBOUND_STATUS_VARIANT = {
  PENDING: "outline",
  IN_PROGRESS: "default",
  COMPLETED: "secondary",
  FAILED: "destructive",
  CANCELLED: "destructive",
} as const;

/**
 * 상담 결과 레이블 가져오기
 * @param result 상담 결과 ('SUCCESS' | 'FAILURE')
 * @returns 한글 레이블
 */
export const getConsultResultLabel = (
  result: string | null | undefined,
): string => {
  if (!result) return "-";
  return (
    CONSULT_RESULT_LABEL[result as keyof typeof CONSULT_RESULT_LABEL] || "-"
  );
};

/**
 * 상담 결과 variant 가져오기
 * @param result 상담 결과
 * @returns Badge variant
 * V197: PENDING variant 추가
 */
export const getConsultResultVariant = (
  result: string | null | undefined,
): "default" | "destructive" | "outline" => {
  if (!result) return "outline";
  return (
    CONSULT_RESULT_VARIANT[result as keyof typeof CONSULT_RESULT_VARIANT] ||
    "outline"
  );
};

/**
 * 예약 상태 레이블 가져오기
 * @param status 예약 상태
 * @returns 한글 레이블
 */
export const getAppointmentStatusLabel = (
  status: string | null | undefined,
): string => {
  if (!status) return "-";
  return (
    APPOINTMENT_STATUS_LABEL[status as keyof typeof APPOINTMENT_STATUS_LABEL] ||
    "-"
  );
};

/**
 * 예약 상태 variant 가져오기
 * @param status 예약 상태
 * @returns Badge variant
 */
export const getAppointmentStatusVariant = (
  status: string | null | undefined,
): "default" | "secondary" | "destructive" | "outline" => {
  if (!status) return "outline";
  return (
    APPOINTMENT_STATUS_VARIANT[
      status as keyof typeof APPOINTMENT_STATUS_VARIANT
    ] || "outline"
  );
};

/**
 * 상담 타입 레이블 가져오기
 * @param type 상담 타입
 * @returns 한글 레이블
 */
export const getConsultTypeLabel = (
  type: keyof typeof CONSULT_TYPE_LABEL,
): string => {
  return CONSULT_TYPE_LABEL[type] || "-";
};

/**
 * 상담 타입 variant 가져오기
 * @param type 상담 타입
 * @returns Badge variant
 */
export const getConsultTypeVariant = (
  type: keyof typeof CONSULT_TYPE_VARIANT,
): "default" | "secondary" | "destructive" | "outline" => {
  return CONSULT_TYPE_VARIANT[type] || "outline";
};

/**
 * V211: 상담 세부 유형 레이블
 * AI가 분석한 통화의 구체적인 목적/내용 분류
 */
export const CONSULT_SUB_TYPE_LABEL = {
  // 예약 관련
  NEW_APPOINTMENT: "신규 예약",
  CHANGE_APPOINTMENT: "예약 변경",
  CANCEL_APPOINTMENT: "예약 취소",
  CHECK_APPOINTMENT: "예약 확인",
  // 불만 관련
  SERVICE_COMPLAINT: "서비스 불만",
  FACILITY_COMPLAINT: "시설 불만",
  STAFF_COMPLAINT: "직원 불만",
  MEDICAL_COMPLAINT: "진료 불만",
  // 문의 관련
  PRICE_INQUIRY: "가격 문의",
  TREATMENT_INQUIRY: "시술/치료 문의",
  INSURANCE_INQUIRY: "보험 문의",
  FACILITY_INQUIRY: "시설 문의",
  LOCATION_INQUIRY: "위치 문의",
  // 기타
  FOLLOW_UP_INQUIRY: "후속 문의",
  EMERGENCY: "긴급",
  POST_SURGERY_COMPLICATION: "수술 후 부작용",
  URGENT_SYMPTOM: "긴급 증상",
  OTHER: "기타",
} as const;

/**
 * V211: 상담 세부 유형에 따른 Badge variant
 * - 예약 변경/취소: warning (주의 필요)
 * - 불만 관련: destructive (빨간색)
 * - 긴급 관련: destructive (빨간색)
 * - 문의 관련: secondary (회색)
 * - 기타: outline (테두리만)
 */
export const CONSULT_SUB_TYPE_VARIANT = {
  // 예약 관련
  NEW_APPOINTMENT: "default",
  CHANGE_APPOINTMENT: "warning", // 주의 필요
  CANCEL_APPOINTMENT: "warning", // 주의 필요
  CHECK_APPOINTMENT: "secondary",
  // 불만 관련
  SERVICE_COMPLAINT: "destructive",
  FACILITY_COMPLAINT: "destructive",
  STAFF_COMPLAINT: "destructive",
  MEDICAL_COMPLAINT: "destructive",
  // 문의 관련
  PRICE_INQUIRY: "secondary",
  TREATMENT_INQUIRY: "secondary",
  INSURANCE_INQUIRY: "secondary",
  FACILITY_INQUIRY: "secondary",
  LOCATION_INQUIRY: "secondary",
  // 기타
  FOLLOW_UP_INQUIRY: "outline",
  EMERGENCY: "destructive",
  POST_SURGERY_COMPLICATION: "destructive",
  URGENT_SYMPTOM: "destructive",
  OTHER: "outline",
} as const;

/**
 * 상담 세부 유형 레이블 가져오기
 * @param subType 상담 세부 유형
 * @returns 한글 레이블
 */
export const getConsultSubTypeLabel = (
  subType: keyof typeof CONSULT_SUB_TYPE_LABEL | null | undefined,
): string => {
  if (!subType) return "-";
  return CONSULT_SUB_TYPE_LABEL[subType] || "-";
};

/**
 * 상담 세부 유형 variant 가져오기
 * @param subType 상담 세부 유형
 * @returns Badge variant
 */
export const getConsultSubTypeVariant = (
  subType: keyof typeof CONSULT_SUB_TYPE_VARIANT | null | undefined,
): "default" | "secondary" | "destructive" | "outline" | "warning" => {
  if (!subType) return "outline";
  return CONSULT_SUB_TYPE_VARIANT[subType] || "outline";
};

/**
 * 예약 변경/취소 관련 세부 유형인지 확인
 * 콜백이나 특별 처리가 필요한 유형
 */
export const isAppointmentChangeOrCancel = (
  subType: keyof typeof CONSULT_SUB_TYPE_LABEL | null | undefined,
): boolean => {
  return subType === "CHANGE_APPOINTMENT" || subType === "CANCEL_APPOINTMENT";
};

/**
 * 예약 접수 상태 레이블 (타임테이블/예약목록에서 사용)
 * AppointmentStatus enum 기반 (REQUESTED → NO_SHOW)
 * Task #39: REQUESTED/CHECKING → "신청"으로 통일 (UI 전반 일관성)
 */
export const BOOKING_STATUS_LABEL: Record<string, string> = {
  REQUESTED: "신청",   // Task #39: "대기" → "신청" (신청 대기 상태)
  CHECKING: "신청",    // Task #39: "확인중" → "신청" (REQUESTED와 동일 그룹)
  CONFIRMED: "확정",
  SMS_SENT: "안내",
  COMPLETED: "완료",
  CANCELLED: "취소",
  NO_SHOW: "노쇼",
  AVAILABLE: "가능",
  NOT_AVAILABLE: "불가",
  NOT_REQUESTED: "해당없음",
} as const;

/**
 * 예약 접수 상태별 Badge 배경색 (Tailwind)
 */
export const BOOKING_STATUS_BADGE_COLOR: Record<string, string> = {
  REQUESTED: "bg-info",
  CHECKING: "bg-sky-500",
  CONFIRMED: "bg-success",
  SMS_SENT: "bg-purple-500",
  COMPLETED: "bg-muted-foreground",
  CANCELLED: "bg-destructive",
  NO_SHOW: "bg-amber-500",
} as const;

/**
 * 예약 접수 상태별 좌측 보더 색상 (카드 인디케이터)
 */
export const BOOKING_STATUS_BORDER: Record<string, string> = {
  REQUESTED: "border-l-info",
  CHECKING: "border-l-sky-500",
  CONFIRMED: "border-l-success",
  SMS_SENT: "border-l-purple-500",
  COMPLETED: "border-l-muted-foreground",
  CANCELLED: "border-l-destructive",
  NO_SHOW: "border-l-amber-500",
} as const;

/**
 * 예약 접수 상태별 배경+텍스트 (Popover/Sheet용)
 */
export const BOOKING_STATUS_COLOR: Record<string, string> = {
  REQUESTED: "bg-info/15 text-info",
  CHECKING: "bg-sky-100 text-sky-800",
  CONFIRMED: "bg-success/15 text-success",
  SMS_SENT: "bg-purple-100 text-purple-800",
  COMPLETED: "bg-muted text-muted-foreground",
  CANCELLED: "bg-destructive/10 text-destructive",
  NO_SHOW: "bg-amber-100 text-amber-800",
} as const;
